import MenuOption from './MenuOption';
import SideMenuList from './SideMenuList';
import { Box, Drawer } from '@mui/material';
import { useEffect, useState } from 'react';
import { Role } from '../../constants/Roles';
import { Routes } from '../../navigation/Routes';
import { useAuth } from '../../auth/hooks/useAuth';
import { ReactComponent as Logo } from '../../assets/procedurehorizontalsmall.svg';
import { ReactComponent as MigrationIcon } from '@src/assets/icons/migration.svg';
import { ReactComponent as ConsultantIcon } from '@src/assets/icons/consultants.svg';
import { ReactComponent as ProceduresIcon } from '@src/assets/icons/procedures.svg';
import { ReactComponent as CardsIcon } from '@src/assets/icons/preferences.svg';
import { ReactComponent as ScanCheckIcon } from '@src/assets/icons/scan_check.svg';
import { ReactComponent as ScanReview } from '@src/assets/icons/scan_review.svg';
import { ReactComponent as UserManagementIcon } from '@src/assets/icons/user_management.svg';
import { useMenu } from '@src/providers/MenuProvider';
import PublicIcon from '@mui/icons-material/Public';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ControlledHospitalAutoComplete from '../hospital/ControlledHospitalAutoComplete';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';

const allTopMenuItems: MenuOption[] = [
  {
    name: 'System',
    route: '',
    children: [
      {
        icon: CorporateFareIcon,
        name: 'Organisations',
        route: Routes.Organisations,
        roles: [Role.Administrator],
      },
      {
        icon: PublicIcon,
        name: 'Global Inventory',
        route: Routes.Inventories,
        roles: [Role.Administrator],
      },
      {
        icon: ConsultantIcon,
        name: 'Consultants',
        route: Routes.Consultants,
        roles: [Role.Administrator],
      },
      {
        icon: ProceduresIcon,
        name: 'Procedures',
        route: Routes.Procedures,
        roles: [Role.Administrator],
      },
      {
        icon: UserManagementIcon,
        name: 'User Management',
        route: Routes.UserManagement,
        roles: [Role.Administrator],
      },
      {
        icon: TaskAltIcon,
        name: 'Audit Log',
        route: Routes.AuditLog,
        roles: [Role.Administrator],
      },
    ],
  },
  {
    name: 'Migration',
    route: '',
    open: true,
    children: [
      {
        icon: ScanCheckIcon,
        name: 'Scan Check',
        route: Routes.ScanCheck,
        roles: [Role.ScanCheck],
      },
      {
        icon: ScanReview,
        name: 'Scan Review',
        route: Routes.ScanReview,
        roles: [Role.ScanReview],
      },
      {
        icon: MigrationIcon,
        name: 'Migration',
        route: Routes.Migration,
        roles: [Role.Migration],
      },
    ],
  },
  {
    name: 'Hospital',
    route: '',
    open: true,
    children: [
      {
        icon: CardsIcon,
        name: 'Cards',
        route: Routes.Cards,
        roles: [Role.PreferenceAdmin],
      },
      {
        icon: CardsIcon,
        name: 'Change Requests',
        route: Routes.ChangeRequests,
        roles: [Role.ChangeRequest],
      },
      {
        icon: CardsIcon,
        name: 'Laying Up Summary',
        route: Routes.LayingUp,
        roles: [Role.LayingUpAdmin],
      },
    ],
  },
];

interface FormValues {
  hospitalId: string | null;
}

function SideMenu() {
  const {
    hasAnyRole,
    setCurrentHospitalId,
    currentHospitalId,
    isMultipleHospitals,
  } = useAuth();
  const { menuWidth } = useMenu();
  const [topMenuItems, setTopMenuItems] = useState<MenuOption[]>([]);

  useEffect(() => {
    if (topMenuItems.length === 0) {
      const filteredMenuItems = allTopMenuItems.filter((value) => {
        let menuItem = value;

        if (
          !menuItem.route &&
          (!menuItem.children || menuItem.children.length === 0)
        ) {
          return false;
        }

        if (menuItem.children && menuItem.children.length > 0) {
          const accessibleChildren = menuItem.children.filter((child) => {
            if (!child.roles || child.roles.length === 0) {
              return child;
            }
            const hasRoles = hasAnyRole(child.roles);
            return hasRoles;
          });

          menuItem.children = accessibleChildren;

          if (menuItem.children && menuItem.children.length > 0) {
            return menuItem;
          } else {
            return false;
          }
        }

        if (!menuItem.roles || menuItem.roles.length === 0) {
          if (menuItem.route && menuItem.route === '') {
            return false;
          } else {
            return menuItem;
          }
        }

        return hasAnyRole(menuItem.roles);
      });

      setTopMenuItems(filteredMenuItems);
    }
  }, [hasAnyRole, topMenuItems]);

  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      hospitalId: currentHospitalId,
    },
  });

  const { control, setValue } = methods;
  const selectedHospitalId = useWatch({ control, name: 'hospitalId' });

  useEffect(() => {
    if (selectedHospitalId) {
      setCurrentHospitalId(selectedHospitalId);
    }
  }, [selectedHospitalId, setCurrentHospitalId]);

  useEffect(() => {
    if (currentHospitalId) {
      setValue('hospitalId', selectedHospitalId);
    }
  }, [currentHospitalId, selectedHospitalId, setValue]);

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={menuWidth !== 0}
      sx={{
        width: menuWidth,
      }}>
      <Box
        sx={(theme) => ({
          padding: theme.spacing(2),
          paddingLeft: theme.spacing(3),
        })}>
        <Logo />
      </Box>
      <Box sx={(theme) => ({ padding: theme.spacing(1) })}>
        <FormProvider {...methods}>
          {isMultipleHospitals && (
            <ControlledHospitalAutoComplete fieldName="hospitalId" />
          )}
        </FormProvider>
      </Box>
      <Box
        sx={{
          overflow: 'auto',
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}>
        <SideMenuList items={topMenuItems} childList={false} />
      </Box>
    </Drawer>
  );
}

export default SideMenu;
